import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../store/slices/authSlice";
import {
  Grid,
  User,
  RotateCcw,
  FileText,
  Code,
  LogOut,
  Users,
  BarChart3,
  Settings,
  X,
  CreditCard,
  CodeXml,
  Activity,
  Percent,
} from "lucide-react";

const Sidebar = ({ activeMenu, setActiveMenu, isOpen, isMobile = false, onClose, menuItems = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.auth);
  const sidebarRef = useRef(null);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        onClose && onClose();
      }
    };

    if (isMobile && isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, isOpen, onClose]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
    if (isMobile) {
      onClose && onClose();
    }
  };

  const handleMenuClick = (itemId, navigatePath) => {
    setActiveMenu(itemId);
    if (navigatePath) {
      navigate(navigatePath);
    }
    if (isMobile) {
      onClose && onClose();
    }
  };


  const adminMenuItems = [
    {
      id: "overview",
      label: "Dashboard",
      icon: <BarChart3 className="w-5 h-5" />,
      navigate: "/admin-dashboard",
    },
    {
      id: "users",
      label: "Users Management",
      icon: <Users className="w-5 h-5" />,
      navigate: "/admin-dashboard/users",
    },
    {
      id: "api-billing",
      label: "API & Billing",
      icon: <CreditCard className="w-5 h-5" />,
      navigate: "/admin-dashboard/api-and-billing",
    },
    {
      id: "api-usage-logs",
      label: "API Usage Logs",
      icon: <Activity className="w-5 h-5" />,
      navigate: "/admin-dashboard/api-usage-logs",
    },
    {
      id: "content-management",
      label: "Content Management",
      icon: <CodeXml className="w-5 h-5" />,
      navigate: "/admin-dashboard/content-management",
    },
    {
      id: "support",
      label: "Support",
      icon: <FileText className="w-5 h-5" />,
      navigate: "/admin-dashboard/support",
    },
    {
      id: "discount-management",
      label: "Discount Management",
      icon: <Percent className="w-5 h-5" />,
      navigate: "/admin-dashboard/discount-management",
    },

  ];

  return (
    <div
      ref={sidebarRef}
      className={`
        fixed left-0 top-0 h-full bg-white shadow-lg transition-all duration-300 ease-in-out z-40
        ${isMobile ? "w-64" : isOpen ? "w-64" : "w-16"}
      `}
    >
      <div className={`h-full flex flex-col ${!isOpen && !isMobile ? "" : "p-6"}`}>
        {/* Logo Section */}
        <div className="flex items-center justify-between mb-8">
          <div className="w-full h-full flex items-center justify-center flex-shrink-0">
            {isOpen || isMobile ? (
              <img
                src="/images/Nkenne Ai-01 2.png"
                alt="NKENNE AI"
                className="w-full h-full object-contain"
              />
            ) : (
              <img
                src="/Nkenne.svg"
                alt="NKENNE"
                className="w-8 h-full object-contain mt-6"
              />
            )}
          </div>
          {isMobile && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="space-y-2">
          {adminMenuItems.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 cursor-pointer ${activeMenu === item.id
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              onClick={() => handleMenuClick(item.id, item.navigate)}
              title={!isOpen && !isMobile ? item.label : ""}
            >
              <div className="flex-shrink-0">{item.icon}</div>
              <span
                className={`font-medium whitespace-nowrap transition-all duration-300 ${isOpen || isMobile ? "opacity-100" : "opacity-0 hidden"
                  }`}
              >
                {item.label}
              </span>
            </div>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="mt-auto pt-8">
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-3 w-full text-left text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all duration-200`}
            title={!isOpen && !isMobile ? "Logout" : ""}
          >
            <div className="flex-shrink-0">
              <LogOut className="w-5 h-5" />
            </div>
            <span
              className={`font-medium whitespace-nowrap transition-all duration-300 ${isOpen || isMobile ? "opacity-100" : "opacity-0 hidden"
                }`}
            >
              Logout
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;